import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Building2, Home as HomeIcon, Briefcase, Calendar, MapPin,
  Phone, Mail, UserCheck, FileText, Save, MessageSquare, CheckCircle2, Shield, Printer, type LucideIcon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Lead, EstadoLead, TipoImovel,
  getLeads, getInstalaldoresComelit, updateLead, InstaladorComelit,
  ESTADO_LABELS, ESTADO_COLORS, TIPO_IMOVEL_LABELS,
  formatDate, formatDateShort, generateLeadCode,
} from '@/lib/index';
import { queueEmail } from '@/lib/airtable';

const TIPO_ICON: Record<TipoImovel, LucideIcon> = {
  predio: Building2,
  moradia: HomeIcon,
  escritorio: Briefcase,
};

export default function LeadDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [lead, setLead] = useState<Lead | null>(null);
  const [instaladores, setInstaladores] = useState<InstaladorComelit[]>([]);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [emailsSent, setEmailsSent] = useState(0);

  // editable fields
  const [estado, setEstado] = useState<EstadoLead>('novo');
  const [instaladorIds, setInstaladorIds] = useState<string[]>([]);
  const [numeroFatura, setNumeroFatura] = useState('');
  const [notasInternas, setNotasInternas] = useState('');

  // Guardar quais instaladores já tinham sido notificados (os que já estavam antes de guardar)
  const [instaladoresNotificados, setInstaladoresNotificados] = useState<string[]>([]);

  // Gerar e imprimir certificado de garantia
  const handlePrintCertificate = () => {
    if (!lead) return;
    const code = generateLeadCode(lead.id);
    const today = new Date();
    const expiryDate = new Date(today);
    expiryDate.setFullYear(today.getFullYear() + 5);
    const fmt = (d: Date) => d.toLocaleDateString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric' });
    const num = code.replace('CTR-', '').padStart(6, '0');
    const html = `<!DOCTYPE html><html lang="pt"><head><meta charset="UTF-8"><title>Certificado de Garantia AT CONTERA — ${code}</title><style>
*{margin:0;padding:0;box-sizing:border-box;}
body{font-family:'Helvetica Neue',Arial,sans-serif;background:#fff;color:#1a1a2e;}
@page{size:A4;margin:0;}
@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact;}}
.page{width:210mm;min-height:297mm;padding:0;position:relative;overflow:hidden;background:#fff;}
.header-bar{background:#1a1a2e;height:8mm;width:100%;}
.content{padding:12mm 14mm;}
.cert-num{font-size:11px;color:#666;letter-spacing:4px;text-transform:uppercase;text-align:right;margin-bottom:6mm;}
.logo-row{display:flex;align-items:center;justify-content:space-between;margin-bottom:8mm;}
.brand{font-size:28px;font-weight:900;color:#1a1a2e;letter-spacing:-1px;}
.brand span{color:#c8a84b;}
.partner{font-size:10px;color:#888;font-weight:600;letter-spacing:2px;text-transform:uppercase;}
.divider{height:3px;background:linear-gradient(90deg,#1a1a2e,#c8a84b,#1a1a2e);margin:4mm 0 8mm;}
.cert-title{text-align:center;margin-bottom:8mm;}
.cert-title h1{font-size:22px;font-weight:900;letter-spacing:6px;text-transform:uppercase;color:#1a1a2e;}
.cert-title p{font-size:11px;color:#888;letter-spacing:3px;text-transform:uppercase;margin-top:2mm;}
.seal{display:flex;align-items:center;justify-content:center;margin:6mm 0;}
.seal-circle{width:28mm;height:28mm;border-radius:50%;border:3px solid #1a1a2e;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;}
.seal-circle .years{font-size:22px;font-weight:900;color:#1a1a2e;line-height:1;}
.seal-circle .label{font-size:7px;letter-spacing:2px;color:#666;text-transform:uppercase;margin-top:1mm;}
.address-box{background:#f8f8f8;border-left:4px solid #1a1a2e;padding:5mm 6mm;margin:6mm 0;border-radius:0 6px 6px 0;}
.address-box .label{font-size:9px;color:#888;letter-spacing:2px;text-transform:uppercase;margin-bottom:2mm;}
.address-box .value{font-size:15px;font-weight:700;color:#1a1a2e;}
.address-box .ref{font-size:10px;color:#666;margin-top:1mm;font-family:monospace;}
.info-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:4mm;margin:5mm 0;}
.info-cell{text-align:center;padding:3mm;border:1px solid #eee;border-radius:6px;}
.info-cell .lbl{font-size:8px;color:#888;text-transform:uppercase;letter-spacing:1px;}
.info-cell .val{font-size:11px;font-weight:700;color:#1a1a2e;margin-top:1mm;}
.signature-area{display:flex;justify-content:space-between;align-items:flex-end;margin-top:8mm;}
.sig-block{text-align:center;}
.sig-line{width:45mm;border-bottom:1px solid #333;margin-bottom:2mm;}
.sig-name{font-size:10px;font-weight:700;color:#1a1a2e;letter-spacing:1px;}
.sig-title{font-size:8px;color:#888;}
.footer-bar{position:absolute;bottom:0;width:100%;background:#1a1a2e;padding:4mm 14mm;display:flex;justify-content:space-between;align-items:center;}
.footer-bar span{color:#fff;font-size:9px;letter-spacing:1px;}
.footer-bar strong{color:#c8a84b;}
.with-you{font-size:8px;letter-spacing:3px;color:#c8a84b;text-transform:uppercase;text-align:center;margin:4mm 0;}
.page2{page-break-before:always;}
.conditions{padding:12mm 14mm;}
.conditions h2{font-size:14px;font-weight:900;text-transform:uppercase;letter-spacing:3px;color:#1a1a2e;margin-bottom:6mm;text-align:center;}
.conditions ol{list-style:none;counter-reset:item;}
.conditions ol li{counter-increment:item;display:flex;gap:3mm;margin-bottom:4mm;font-size:10px;line-height:1.5;color:#333;}
.conditions ol li::before{content:counter(item)".";font-weight:700;color:#1a1a2e;min-width:6mm;}
.important{background:#1a1a2e;color:#fff;text-align:center;padding:4mm;font-size:9px;font-weight:700;letter-spacing:2px;text-transform:uppercase;margin-top:8mm;}
</style></head><body>
<div class="page">
  <div class="header-bar"></div>
  <div class="content">
    <div class="cert-num">CERTIFICADO Nº ${num}</div>
    <div class="logo-row">
      <div>
        <div class="brand">AT <span>CONTERA</span></div>
        <div class="partner">Distribuidor Oficial Comelit • Portugal</div>
      </div>
      <div style="text-align:right">
        <div style="font-size:16px;font-weight:900;color:#1a1a2e;letter-spacing:2px;">COMELIT</div>
        <div style="font-size:8px;color:#888;letter-spacing:1px;">VIDEO DOOR ENTRY SYSTEMS</div>
      </div>
    </div>
    <div class="divider"></div>
    <div class="cert-title">
      <h1>Certificado de Garantia</h1>
      <p>AT CONTERA / COMELIT — Documento Oficial</p>
    </div>
    <div class="seal">
      <div class="seal-circle">
        <div class="years">5</div>
        <div class="label">anos<br/>garantia</div>
      </div>
    </div>
    <div class="address-box">
      <div class="label">Imóvel / Instalação</div>
      <div class="value">${lead.nome}</div>
      <div class="value" style="font-size:13px;font-weight:600;margin-top:1mm;">${lead.morada}${lead.codigoPostal ? ', ' + lead.codigoPostal : ''} ${lead.localidade}</div>
      <div class="ref">Referência da obra: ${code}</div>
    </div>
    <div class="info-grid">
      <div class="info-cell"><div class="lbl">Data de Emissão</div><div class="val">${fmt(today)}</div></div>
      <div class="info-cell"><div class="lbl">Validade</div><div class="val">5 Anos</div></div>
      <div class="info-cell"><div class="lbl">Expira em</div><div class="val">${fmt(expiryDate)}</div></div>
    </div>
    <p style="font-size:9px;color:#555;line-height:1.6;margin:4mm 0;text-align:center;font-style:italic;">
      Ao adquirir um produto Comelit através da rede AT CONTERA, o comprador reconhece e concorda com todos os termos e condições estabelecidos neste Certificado de Garantia.
    </p>
    <div class="with-you">WITH YOU ALWAYS</div>
    <div class="signature-area">
      <div class="sig-block">
        <div class="sig-line"></div>
        <div class="sig-name">ANTÓNIO MOREIRA</div>
        <div class="sig-title">Diretor — AT CONTERA</div>
      </div>
      <div style="text-align:center;">
        <div style="font-size:8px;color:#888;letter-spacing:1px;">CERTIFICADO VÁLIDO COM CARIMBO</div>
      </div>
    </div>
  </div>
  <div class="footer-bar">
    <span>at@contera.pt</span>
    <strong>AT CONTERA</strong>
    <span>www.atcontera.pt</span>
  </div>
</div>
<div class="page page2">
  <div class="header-bar"></div>
  <div class="conditions">
    <h2>Certificado de Garantia CONTERA/COMELIT — Condições</h2>
    <ol>
      <li>A Contera é representante exclusivo para Portugal da marca Comelit.</li>
      <li>Todos os equipamentos COMELIT adquiridos à rede Contera a partir de 01.07.2023 e até 31.12.2028, terão a garantia de 5 anos contra defeitos de fabrico.</li>
      <li>O cliente final (promotor, condomínio ou cliente individual) diretamente ou através do seu distribuidor ou instalador devem solicitar o certificado de garantia.</li>
      <li>A garantia só será válida caso o sistema seja instalado por um instalador oficial Contera/Comelit e com número de cartão ativo.</li>
      <li>Este registo é obrigatório para poder ter acesso à extensão da garantia e é da responsabilidade do cliente manter o mesmo na sua propriedade.</li>
      <li>Informa-se também que os equipamentos adquiridos fora do mercado nacional e fora da rede autorizada Contera/Comelit em Portugal, não terão qualquer garantia legal ou qualquer extensão e não serão da responsabilidade do importador oficial.</li>
    </ol>
    <div class="important">COMPRE SOMENTE A INSTALADORES E DISTRIBUIDORES AUTORIZADOS COMELIT</div>
  </div>
  <div class="footer-bar">
    <span>at@contera.pt</span>
    <strong>AT CONTERA</strong>
    <span>www.atcontera.pt</span>
  </div>
</div>
<script>window.onload=function(){window.print();}</script>
</body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); }
  };

  useEffect(() => {
    async function load() {
      const [leads, insListComelit] = await Promise.all([getLeads(), getInstalaldoresComelit()]);
      const found = leads.find((l) => l.id === id) ?? null;
      setLead(found);
      if (found) {
        setEstado(found.estado);
        const ids = found.instaladorIds ?? (found.instaladorId ? [found.instaladorId] : []);
        setInstaladorIds(ids);
        setInstaladoresNotificados(ids);
        setNumeroFatura(found.numeroFatura ?? '');
        setNotasInternas(found.notasInternas ?? '');
      }
      // Mostrar apenas instaladores aprovados
      setInstaladores(insListComelit.filter(i => i.status === 'aprovado'));
    }
    load();
  }, [id]);

  const handleSave = async () => {
    if (!id || !lead) return;
    setSaving(true);
    setEmailsSent(0);

    const updated = await updateLead(id, {
      estado,
      instaladorIds: instaladorIds.length > 0 ? instaladorIds : undefined,
      instaladorId: instaladorIds[0] ?? undefined,
      numeroFatura: numeroFatura || undefined,
      notasInternas: notasInternas || undefined,
    });

    if (updated) {
      // Enviar email apenas aos instaladores NOVOS (não notificados anteriormente) — via Airtable EmailQueue
      const novosInstaladores = instaladorIds.filter((iid) => !instaladoresNotificados.includes(iid));
      for (const iid of novosInstaladores) {
        const ins = instaladores.find((i) => i.id === iid);
        if (!ins?.email) continue;
        const code = generateLeadCode(lead.id);
        await queueEmail({
          email: ins.email,
          nome:  ins.nome,
          assunto: `[AT CONTERA] Nova obra atribuída — ${code} — ${lead.localidade}`,
          mensagem: [
            `Olá ${ins.nome},`,
            ``,
            `A AT CONTERA atribuiu-lhe uma nova obra. Por favor entre em contacto com o cliente para agendar a visita.`,
            ``,
            `══════════════════════════════════`,
            `📋 REFERÊNCIA DA OBRA: ${code}`,
            `══════════════════════════════════`,
            ``,
            `👤 CLIENTE`,
            `   Nome: ${lead.nome}`,
            `   Telefone: ${lead.telefone}`,
            `   Email: ${lead.email}`,
            ``,
            `📍 LOCALIZAÇÃO`,
            `   Morada: ${lead.morada}`,
            `   Localidade: ${lead.localidade} · ${lead.codigoPostal}`,
            `   Tipo de imóvel: ${{ predio: 'Prédio', moradia: 'Moradia', escritorio: 'Escritório' }[lead.tipoImovel]}`,
            ``,
            `📅 DATA PREFERIDA PARA VISITA: ${lead.dataVisita ? new Date(lead.dataVisita).toLocaleDateString('pt-PT') : '—'}`,
            lead.notas ? `\n📝 NOTAS DO CLIENTE:\n   ${lead.notas}` : '',
            ``,
            `──────────────────────────────────`,
            `🛡️  GARANTIA AT CONTERA`,
            ``,
            `No final da obra, a AT CONTERA emite a Garantia Oficial de 5 Anos ao cliente.`,
            `Esta garantia é assegurada pela AT CONTERA — mais de 6 décadas no mercado.`,
            ``,
            `Guarde sempre a referência da obra: ${code}`,
            ``,
            `📞 Dúvidas: comercial@contera.pt`,
            `──────────────────────────────────`,
            `AT CONTERA · Instaladores Certificados Comelit`,
          ].filter(Boolean).join('\n'),
        });
      }

      // Atualizar lista de notificados
      setInstaladoresNotificados(instaladorIds);
      setLead(updated);
      setEmailsSent(novosInstaladores.length);
      setSaved(true);
      setSaving(false);
      setTimeout(() => { setSaved(false); setEmailsSent(0); }, 3500);
    } else {
      setSaving(false);
    }
  };

  const toggleInstalador = (id: string) => {
    setInstaladorIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  if (!lead) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Pedido não encontrado.</p>
          <Button variant="outline" onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={15} className="mr-2" />Voltar ao Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const TipoIcon = TIPO_ICON[lead.tipoImovel];
  const instaladoresSelecionados = instaladores.filter((i) =>
    instaladorIds.includes(i.id) || lead.instaladorIds?.includes(i.id) || lead.instaladorId === i.id
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-border bg-card/90 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={15} className="mr-2" />Voltar
          </Button>
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm text-muted-foreground">{generateLeadCode(lead.id)}</span>
            <Badge className={`text-xs border ${ESTADO_COLORS[lead.estado]}`} variant="outline">
              {ESTADO_LABELS[lead.estado]}
            </Badge>
          </div>
          <Button size="sm" onClick={handleSave} disabled={saved || saving}>
            {saving ? (
              <><span className="animate-spin mr-1.5">⏳</span>A guardar...</>
            ) : saved ? (
              <><CheckCircle2 size={14} className="mr-1.5 text-green-300" />{emailsSent > 0 ? `Guardado · ${emailsSent} email${emailsSent > 1 ? 's' : ''} enviado${emailsSent > 1 ? 's' : ''}` : 'Guardado'}</>
            ) : (
              <><Save size={14} className="mr-1.5" />Guardar</>
            )}
          </Button>
        </div>
      </header>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 35 }}
        className="max-w-4xl mx-auto px-4 py-8"
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: dados do cliente */}
          <div className="lg:col-span-2 space-y-5">
            {/* Cliente */}
            <div className="bg-card border border-border rounded-2xl p-5">
              <h2 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <Phone size={16} className="text-primary" />
                Dados do Cliente
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Nome</p>
                  <p className="font-medium text-foreground mt-0.5">{lead.nome}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Telefone</p>
                  <p className="font-medium text-foreground mt-0.5 flex items-center gap-1">
                    <Phone size={12} className="text-muted-foreground" />{lead.telefone}
                  </p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-xs text-muted-foreground">Email</p>
                  <p className="font-medium text-foreground mt-0.5 flex items-center gap-1">
                    <Mail size={12} className="text-muted-foreground" />{lead.email}
                  </p>
                </div>
              </div>
            </div>

            {/* Localização */}
            <div className="bg-card border border-border rounded-2xl p-5">
              <h2 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <MapPin size={16} className="text-primary" />
                Localização e Imóvel
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="sm:col-span-2">
                  <p className="text-xs text-muted-foreground">Morada</p>
                  <p className="font-medium text-foreground mt-0.5">{lead.morada}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Localidade</p>
                  <p className="font-medium text-foreground mt-0.5">{lead.localidade}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Código Postal</p>
                  <p className="font-medium text-foreground mt-0.5 font-mono">{lead.codigoPostal}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Tipo de Imóvel</p>
                  <p className="font-medium text-foreground mt-0.5 flex items-center gap-1.5">
                    <TipoIcon size={14} className="text-muted-foreground" />
                    {TIPO_IMOVEL_LABELS[lead.tipoImovel]}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Data de Visita Preferida</p>
                  <p className="font-medium text-foreground mt-0.5 flex items-center gap-1.5">
                    <Calendar size={14} className="text-muted-foreground" />
                    {formatDateShort(lead.dataVisita)}
                  </p>
                </div>
              </div>
              {lead.notas && (
                <div className="mt-3 pt-3 border-t border-border">
                  <p className="text-xs text-muted-foreground mb-1">Notas do cliente</p>
                  <p className="text-sm text-foreground bg-muted/50 rounded-lg p-3 leading-relaxed">{lead.notas}</p>
                </div>
              )}
            </div>

            {/* Notas internas */}
            <div className="bg-card border border-border rounded-2xl p-5">
              <h2 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <MessageSquare size={16} className="text-primary" />
                Notas Internas
              </h2>
              <Textarea
                placeholder="Anotações internas da equipa AT CONTERA..."
                className="resize-none h-28"
                value={notasInternas}
                onChange={(e) => setNotasInternas(e.target.value)}
              />
            </div>
          </div>

          {/* Right: gestão */}
          <div className="space-y-5">
            {/* Estado */}
            <div className="bg-card border border-border rounded-2xl p-5">
              <h2 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <FileText size={16} className="text-primary" />
                Gestão do Pedido
              </h2>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Estado</Label>
                  <Select value={estado} onValueChange={(v) => setEstado(v as EstadoLead)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.entries(ESTADO_LABELS) as [EstadoLead, string][]).map(([v, l]) => (
                        <SelectItem key={v} value={v}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Instaladores atribuídos</Label>
                  {instaladores.length === 0 ? (
                    <p className="text-xs text-muted-foreground bg-muted/40 rounded-lg p-3">
                      Sem instaladores disponíveis. Aprove instaladores na aba "Rede Comelit" do dashboard.
                    </p>
                  ) : (
                    <div className="border border-border rounded-xl overflow-hidden">
                      {instaladores.map((ins, idx) => {
                        const selected = instaladorIds.includes(ins.id);
                        const jaNotificado = instaladoresNotificados.includes(ins.id);
                        return (
                          <button
                            key={ins.id}
                            type="button"
                            onClick={() => toggleInstalador(ins.id)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${idx > 0 ? 'border-t border-border' : ''} ${selected ? 'bg-primary/8' : 'hover:bg-muted/50'}`}
                          >
                            <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${selected ? 'bg-primary border-primary' : 'border-border'}`}>
                              {selected && <CheckCircle2 size={10} className="text-primary-foreground" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium truncate ${selected ? 'text-foreground' : 'text-muted-foreground'}`}>{ins.nome}</p>
                              <p className="text-xs text-muted-foreground">{ins.zona}</p>
                            </div>
                            {jaNotificado && (
                              <Badge className="text-xs bg-blue-50 text-blue-600 border border-blue-200 shrink-0">📧 Notificado</Badge>
                            )}
                            {selected && !jaNotificado && (
                              <Badge className="text-xs bg-orange-50 text-orange-600 border border-orange-200 shrink-0">Novo ✓</Badge>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {instaladorIds.length > 0 && (
                    <p className="text-xs text-muted-foreground">{instaladorIds.length} instalador{instaladorIds.length > 1 ? 'es' : ''} selecionado{instaladorIds.length > 1 ? 's' : ''}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Garantia */}
            <div className="bg-green-50 border border-green-200 rounded-2xl p-5">
              <h2 className="font-semibold text-green-800 mb-1 flex items-center gap-2">
                <Shield size={16} className="text-green-600" />
                Garantia AT CONTERA — 5 Anos
              </h2>
              <p className="text-xs text-green-700 mb-4 leading-relaxed">
                No final da obra, a AT CONTERA emite a garantia oficial de 5 anos ao cliente. Registe aqui o nº de fatura para activar e arquivar a garantia.
              </p>
              <div className="space-y-1.5">
                <Label className="text-xs">Número de Fatura</Label>
                <Input
                  placeholder="Ex: FT 2026/00123"
                  className="font-mono"
                  value={numeroFatura}
                  onChange={(e) => setNumeroFatura(e.target.value)}
                />
              </div>
              {lead.numeroFatura && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-xs text-green-700 font-medium flex items-center gap-1.5">
                    <CheckCircle2 size={13} />Fatura registada
                  </p>
                  <p className="font-mono text-sm text-green-800 font-semibold mt-1">{lead.numeroFatura}</p>
                </div>
              )}
              <button
                type="button"
                onClick={handlePrintCertificate}
                className="mt-4 w-full flex items-center justify-center gap-2 bg-green-700 hover:bg-green-800 text-white font-semibold text-sm py-2.5 px-4 rounded-xl transition-colors"
              >
                <Printer size={15} />
                Imprimir Certificado de Garantia
              </button>
            </div>

          </div>
        </div>
      </motion.div>
    </div>
  );
}
